import { RouteComponentProps } from "@reach/router"
import { createContext, PropsWithChildren, useContext, useMemo } from "react"
import { GetPeriodsOutput } from "../api/insights/periods/utils"
import { FleetEntryWithMileage } from "../api/rebates/useGetFleetEntriesWithMileage"

export type InsightsContextType = {
  periods?: GetPeriodsOutput
  fleetEntriesWithMileage?: ReadonlyArray<FleetEntryWithMileage>
  insightsProviderLoading?: boolean
  fleetEntriesProviderLoading?: boolean
  v2AccumulatedMileage?: number | null
}

export const InsightsContext = createContext<InsightsContextType | undefined>(undefined)

type ProtectedInsightsProviderProps = {
  periods?: GetPeriodsOutput
  fleetEntriesWithMileage?: ReadonlyArray<FleetEntryWithMileage>
  insightsProviderLoading?: boolean
  fleetEntriesProviderLoading?: boolean
  v2AccumulatedMileage?: number | null
}

const ProtectedInsightsProvider = ({
  periods,
  fleetEntriesWithMileage,
  insightsProviderLoading,
  fleetEntriesProviderLoading,
  v2AccumulatedMileage,
  children,
}: PropsWithChildren<ProtectedInsightsProviderProps>) => {
  const value = useMemo<InsightsContextType>(
    () => ({
      periods,
      fleetEntriesWithMileage,
      insightsProviderLoading,
      fleetEntriesProviderLoading,
      v2AccumulatedMileage,
    }),
    [periods, fleetEntriesWithMileage, insightsProviderLoading, fleetEntriesProviderLoading, v2AccumulatedMileage]
  )
  return <InsightsContext.Provider value={value}>{children}</InsightsContext.Provider>
}

ProtectedInsightsProvider.defaultProps = {
  periods: undefined,
  fleetEntriesWithMileage: undefined,
  insightsProviderLoading: undefined,
  fleetEntriesProviderLoading: undefined,
  v2AccumulatedMileage: undefined,
}

export const InsightsProvider = ({ children }: PropsWithChildren<RouteComponentProps>) => (
  <ProtectedInsightsProvider
    {...{
      periods: undefined,
      fleetEntriesWithMileage: [],
      insightsProviderLoading: false,
      fleetEntriesProviderLoading: false,
      v2AccumulatedMileage: null,
    }}
  >
    {children}
  </ProtectedInsightsProvider>
)

export const useInsights = () => {
  const context = useContext(InsightsContext)
  if (context === undefined) {
    throw new Error(`useInsights must be used inside InsightsProvider.`)
  }
  return context
}
